import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { api, eur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { TrendingUp, Wallet, Users, Percent, Trophy, Target } from "lucide-react";
import { PortfolioPanel } from "../components/re/PortfolioPanel";

const STAGE_COLORS = {
  por_contactar: "#3B82F6", contactado: "#8B5CF6", visitas: "#F59E0B",
  propostas: "#C9A84C", perdido: "#EF4444", representar_outro: "#10B981",
};

function KPI({ icon: Icon, label, value, sub }) {
  return (
    <div className="card-almar p-5 transition-all hover:-translate-y-0.5" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
        <span className="w-9 h-9 rounded-lg bg-[#C9A84C]/15 flex items-center justify-center text-[#C9A84C]"><Icon size={18} /></span>
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-100">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [consultantId, setConsultantId] = useState("");
  const [period, setPeriod] = useState("");

  useEffect(() => {
    if (["admin", "diretor"].includes(user.role))
      api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "consultor"))).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    const params = {};
    if (consultantId) params.consultant_id = consultantId;
    if (period) params.period = period;
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data)).catch(() => {});
  }, [consultantId, period]);

  return (
    <Layout title="Painel de Desempenho">
      <PortfolioPanel />
      {["admin", "diretor"].includes(user.role) && (
        <div className="card-almar p-4 mb-5 flex flex-col sm:flex-row gap-3 sm:items-center">
          <span className="text-xs uppercase tracking-wider text-[#C9A84C]">Filtros</span>
          <select className="input-almar sm:w-64" value={consultantId} onChange={(e) => setConsultantId(e.target.value)} data-testid="dashboard-consultant-filter">
            <option value="">Todos os consultores</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select className="input-almar sm:w-48" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="dashboard-period-filter">
            <option value="">Todo o período</option>
            <option value="mes">Último mês</option>
            <option value="trimestre">Último trimestre</option>
            <option value="ano">Último ano</option>
          </select>
        </div>
      )}
      {!stats ? (
        <div className="text-slate-400">A carregar...</div>
      ) : (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={TrendingUp} label="Volume de Vendas" value={eur(stats.sales_volume)} sub="Propostas em curso" />
        <KPI icon={Wallet} label="Comissões" value={eur(stats.commissions)} sub={`Pendente: ${eur(stats.pending_commissions)}`} />
        <KPI icon={Users} label="Leads Ativas" value={stats.total_leads} sub="Total na carteira" />
        <KPI icon={Percent} label="Taxa de Conversão" value={`${stats.conversion_rate}%`} sub="Propostas / fechadas" />
      </div>

      {stats.goal > 0 && (
        <div className="card-almar p-5 mt-4" data-testid="goal-card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-lg text-slate-100 flex items-center gap-2"><Target size={18} className="text-[#C9A84C]" /> Meta Mensal</h3>
            <span className="text-sm text-[#E0C574] font-semibold">{stats.goal_progress}%</span>
          </div>
          <div className="h-3 rounded-full bg-[#0D1B2A] border border-[#1E3A56] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, stats.goal_progress)}%`, background: "linear-gradient(90deg,#9B7B2B,#C9A84C,#E0C574)" }} />
          </div>
          <p className="text-xs text-slate-400 mt-2">{eur(stats.sales_volume)} de {eur(stats.goal)}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="card-almar p-5 lg:col-span-2">
          <h3 className="font-display text-lg text-slate-100 mb-4">Desempenho Mensal</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={stats.monthly}>
              <defs>
                <linearGradient id="gVendas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C9A84C" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#C9A84C" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E3A56" />
              <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#0D1B2A", border: "1px solid #C9A84C", borderRadius: 8, color: "#fff" }}
                formatter={(v) => eur(v)} />
              <Area type="monotone" dataKey="vendas" stroke="#C9A84C" strokeWidth={2} fill="url(#gVendas)" name="Vendas" />
              <Area type="monotone" dataKey="comissoes" stroke="#38BDF8" strokeWidth={2} fill="transparent" name="Comissões" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card-almar p-5">
          <h3 className="font-display text-lg text-slate-100 mb-4">Pipeline de Vendas</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.pipeline} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E3A56" horizontal={false} />
              <XAxis type="number" stroke="#64748B" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="stage" stroke="#94A3B8" fontSize={10} width={90} />
              <Tooltip contentStyle={{ background: "#0D1B2A", border: "1px solid #C9A84C", borderRadius: 8 }} cursor={{ fill: "rgba(201,168,76,0.08)" }} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]} name="Leads">
                {stats.pipeline.map((p) => <Cell key={p.key} fill={STAGE_COLORS[p.key]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {["admin", "diretor"].includes(user.role) && stats.ranking && (
        <div className="card-almar p-5 mt-6" data-testid="ranking-section">
          <h3 className="font-display text-lg text-slate-100 mb-4 flex items-center gap-2"><Trophy size={18} className="text-[#C9A84C]" /> Ranking de Consultores</h3>
          <div className="space-y-2">
            {stats.ranking.map((r, i) => (
              <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-[#0D1B2A] border border-[#1E3A56]">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-[#C9A84C] text-[#0D1B2A]" : "bg-slate-800 text-slate-400"}`}>{i + 1}</span>
                <img src={r.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-200 text-sm font-medium truncate">{r.name}</span>
                    <span className="text-[#E0C574] font-semibold text-sm ml-2 whitespace-nowrap">{eur(r.volume)}</span>
                  </div>
                  {r.goal > 0 && (
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-[#152A3E] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(100, r.progress)}%`, background: "linear-gradient(90deg,#C9A84C,#E0C574)" }} />
                      </div>
                      <span className="text-[10px] text-slate-500 whitespace-nowrap">{r.progress}% de {eur(r.goal)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </Layout>
  );
}
